import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, MapPinIcon, CheckCircleIcon, PenToolIcon, CalendarIcon, HeartIcon, GlobeIcon, StoreIcon, BookIcon } from 'lucide-react';
import { Bookstore } from '../../data/bookstoresData';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useBrand } from '../../hooks/useBrand';
import SaveToFavorites from '../UI/SaveToFavorites';
interface BookstoreCardProps {
  bookstore: Bookstore;
  viewMode: 'grid' | 'list';
}
// Solid badge colors for the card (like genre badges on UnifiedBookCard)
const getSolidTypeColor = (type: string): string => {
  switch (type) {
    case 'brick-and-mortar':
      return 'bg-emerald-600';
    case 'online-only':
      return 'bg-blue-600';
    case 'hybrid':
      return 'bg-purple-600';
    case 'pop-up':
      return 'bg-amber-600';
    case 'specialty':
      return 'bg-rose-600';
    default:
      return 'bg-gray-600';
  }
};
// Semi-transparent overlay badge colors
const getOverlayTypeColor = (type: string): string => {
  switch (type) {
    case 'brick-and-mortar':
      return 'bg-emerald-500/80';
    case 'online-only':
      return 'bg-blue-500/80';
    case 'hybrid':
      return 'bg-purple-500/80';
    case 'pop-up':
      return 'bg-amber-500/80';
    case 'specialty':
      return 'bg-rose-500/80';
    default:
      return 'bg-gray-500/80';
  }
};
const formatType = (type: string) => {
  return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
export const BookstoreCard: React.FC<BookstoreCardProps> = ({
  bookstore,
  viewMode
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    theme
  } = useBrand();
  const [imgError, setImgError] = useState(false);
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'brick-and-mortar':
        return dk.isDark ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800' : 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'online-only':
        return dk.isDark ? 'bg-blue-900/40 text-blue-300 border-blue-800' : 'bg-blue-100 text-blue-800 border-blue-200';
      case 'hybrid':
        return dk.isDark ? 'bg-purple-900/40 text-purple-300 border-purple-800' : 'bg-purple-100 text-purple-800 border-purple-200';
      case 'pop-up':
        return dk.isDark ? 'bg-amber-900/40 text-amber-300 border-amber-800' : 'bg-amber-100 text-amber-800 border-amber-200';
      case 'specialty':
        return dk.isDark ? 'bg-rose-900/40 text-rose-300 border-rose-800' : 'bg-rose-100 text-rose-800 border-rose-200';
      default:
        return dk.isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getBadgeStyle = () => {
    return dk.isDark ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200';
  };
  const verifiedColor = theme.accent.verified;
  const buttonColor = theme.accent.button;
  // List view (unchanged)
  if (viewMode === 'list') {
    return <div className={`group relative flex items-center p-4 rounded-xl border transition-all duration-200 ${dk.isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm hover:shadow'}`} data-id="element-1137" data-mp="3dhd5n6cg09q">
        <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden mr-4 border border-gray-200 dark:border-gray-700" data-id="element-1138" data-mp="3brzg5ahfgpmh">
          <img src={bookstore.logo} alt={bookstore.name} className="w-full h-full object-cover" data-id="element-1139" data-mp="3gsnfcjft8crs" />
        </div>

        <div className="flex-1 min-w-0 mr-4" data-id="element-1140" data-mp="vzaa3g4ykkol">
          <div className="flex items-center gap-2 mb-1" data-id="element-1141" data-mp="101nea0424aj9">
            <h3 className={`font-bold text-base sm:text-lg truncate ${dk.textPrimary}`} data-id="element-1142" data-mp="1002i6623gjja">
              {bookstore.name}
            </h3>
            {bookstore.verified && <CheckCircleIcon className={`h-4 w-4 flex-shrink-0 ${verifiedColor}`} data-id="element-1143" data-mp="tmycewtjgsx1" />}
            <span className={`text-[10px] px-2 py-0.5 ${rt.badgeRadius} font-medium border ${getTypeColor(bookstore.type)} hidden sm:inline-block`} data-id="element-1144" data-mp="18gbb02a763hb">
              {formatType(bookstore.type)}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm mb-1" data-id="element-1145" data-mp="1cjhonuzn2wxt">
            <div className={`flex items-center ${dk.textSecondary}`} data-id="element-1146" data-mp="1r8vyt5b1hie9">
              <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-1147" data-mp="1rb8h5jkpluru" />
              <span className="truncate" data-id="element-1148" data-mp="1mb3x5jklehwv">
                {bookstore.city ? `${bookstore.city}, ` : ''}
                {bookstore.country}
              </span>
            </div>
            <div className="flex items-center text-yellow-500" data-id="element-1149" data-mp="1w8s1n18033s8">
              <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-1150" data-mp="1w6fbi0s39n43" />
              <span className="font-bold" data-id="element-1151" data-mp="2ex2a4dbo1b3j">{bookstore.rating}</span>
              <span className={`ml-1 text-xs ${dk.textMuted}`} data-id="element-1152" data-mp="2jw5ctr46b58c">
                ({bookstore.reviewCount})
              </span>
            </div>
          </div>

          {bookstore.address && <p className={`text-xs mb-2 truncate ${dk.textMuted}`} data-id="element-1153" data-mp="2aufncqtuqgd7">
              {bookstore.address}
            </p>}

          <div className="hidden sm:flex flex-wrap gap-1.5" data-id="element-1154" data-mp="1w59iam8c1vxe">
            {bookstore.specializations.slice(0, 3).map((spec, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-md ${getBadgeStyle()}`} data-id="element-1155" data-mp="20ym5qt19zwok" data-mpi={idx}>
                {spec}
              </span>)}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end gap-2" data-id="element-1156" data-mp="1d6nhl8gqa6u0">
          <Link to={`/bookstores/${bookstore.slug}`} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${buttonColor}`} data-id="element-1157" data-mp="1i6a468ig4zfh">
            View Store
          </Link>
          <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-1158" data-mp="3f47glcdnl3pw">
            <BookIcon className="h-3 w-3 mr-1" data-id="element-1159" data-mp="3b35coi8sk3hl" />
            {bookstore.bookCount} books
          </div>
        </div>
      </div>;
  }
  // Grid View — matches UnifiedBookCard pattern exactly
  return <div className="relative group w-full" data-id="element-1160" data-mp="6oxcxzu47qg0">
      <Link to={`/bookstores/${bookstore.slug}`} className="block relative aspect-square rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover:scale-105" data-id="element-1161" data-mp="6nmdgc7pd4up">
        {/* Store image */}
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-1162" data-mp="3ousjvd49uwf5">
            <StoreIcon className="h-10 w-10 text-indigo-400 mb-2" data-id="element-1163" data-mp="2ig1f2yc12rfu" />
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-1164" data-mp="2df3xamuxjzev">
              {bookstore.name}
            </span>
          </div> : <img src={bookstore.bannerImage || bookstore.logo} alt={bookstore.name} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-1165" data-mp="28ajef8rqd9h4" />}

        {/* Type badge — top left, solid color (like genre badge) */}
        <span className={`absolute top-2 left-2 text-white text-xs ${getSolidTypeColor(bookstore.type)} px-2 py-0.5 ${rt.badgeRadius} z-10`} data-id="element-1166" data-mp="2m2psmpbbp6dx">
          {formatType(bookstore.type)}
        </span>

        {/* Verified badge — bottom left (like content warning icon position) */}
        {bookstore.verified && <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white shadow-md z-10" data-id="element-1167" data-mp="30yozjwmp50hh">
            <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-1168" data-mp="16knz57bi05fa" />
          </span>}

        {/* Hover Overlay — pure CSS group-hover, matches UnifiedBookCard */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover:opacity-100" data-id="element-1169" data-mp="38gn37ki75hcs">
          {/* Top section */}
          <div className="min-w-0" data-id="element-1170" data-mp="3iam0o67bscye">
            {/* Name + verified */}
            <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1171" data-mp="3daregfyhwbjo">
              <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-1172" data-mp="2pjc6o7ny9211">
                {bookstore.name}
              </h3>
              {bookstore.verified && <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-400" data-id="element-1173" data-mp="2kn3v148ynqym" />}
            </div>

            {/* Type badge in overlay */}
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium mb-1.5 text-white ${getOverlayTypeColor(bookstore.type)}`} data-id="element-1174" data-mp="2ui5lq4ivx13b">
              {formatType(bookstore.type)}
            </span>

            {/* Location — city + country only, NO address */}
            <div className="text-[11px] text-gray-300 flex items-center mb-1.5" data-id="element-1175" data-mp="hbfqny9k18ui">
              <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-1176" data-mp="mcd5c0vcvkr9" />
              <span className="line-clamp-1" data-id="element-1177" data-mp="7ekpt8pt6wd2">
                {bookstore.city ? `${bookstore.city}, ` : ''}
                {bookstore.country}
              </span>
            </div>

            {/* Specialization tags */}
            <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-1178" data-mp="103rruhoh2432">
              {bookstore.specializations.slice(0, 4).map((spec, idx) => <span key={idx} className="px-1.5 py-0.5 bg-indigo-500/80 text-white rounded text-[10px]" data-id="element-1179" data-mp="153w5lzxzwkpl" data-mpi={idx}>
                  {spec}
                </span>)}
              {bookstore.specializations.length > 4 && <span className="px-1.5 py-0.5 bg-white/15 text-gray-300 rounded text-[10px]" data-id="element-1180" data-mp="1j0189bmuj6q8">
                  +{bookstore.specializations.length - 4}
                </span>}
            </div>

            {/* Shipping only (from features) */}
            {bookstore.features.internationalShipping && <div className="text-[11px] text-gray-300 flex items-center mb-1.5" data-id="element-1181" data-mp="25j50y3nmf84y">
                <GlobeIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-1182" data-mp="1rl873f0jxftz" />
                <span data-id="element-1183" data-mp="1wj9rc39prl71">Ships Internationally</span>
              </div>}
          </div>

          {/* Bottom section — rating left, book count right */}
          <div className="mt-auto flex items-center justify-between pt-1" data-id="element-1184" data-mp="1mmww30yisza6">
            <div className="flex items-center gap-1" data-id="element-1185" data-mp="18uyt77vejzmg">
              <StarIcon className="h-4 w-4 text-amber-400" data-id="element-1186" data-mp="18w9u8zxgqt9v" />
              <span className="text-[11px] font-medium" data-id="element-1187" data-mp="1dttj0trseog2">
                {bookstore.rating}
              </span>
              {bookstore.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-1188" data-mp="yyvsm6g7891m">
                  ({bookstore.reviewCount})
                </span>}
            </div>
            <div className="flex items-center gap-1.5" data-id="element-1189" data-mp="kb56d2vkjkxo">
              <div className="bg-white/15 rounded-full p-1" title="Books available" data-id="element-1190" data-mp="kbx0sadfjcmd">
                <BookIcon className="h-3 w-3 text-white" data-id="element-1191" data-mp="u8jmt2hhs9pz" />
              </div>
              <span className="text-[11px] text-gray-300" data-id="element-1192" data-mp="p9ghdgxlo1hh">
                {bookstore.bookCount}
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* Save heart — top right, outside the Link (like book cards) */}
      <div className="absolute top-2 right-2 z-30" data-id="element-1193" data-mp="z6uw9c3vki7o">
        <SaveToFavorites itemId={bookstore.id} size="sm" data-id="element-1194" data-mp="1cyhtbe5n5uri" />
      </div>
    </div>;
};